/**
 * MSAL (Microsoft Authentication Library) Configuration
 *
 * Configures the MSAL instance for Azure Entra ID authentication.
 * Client ID and Tenant ID are read from runtime config (/api/config)
 * instead of build-time VITE_* env vars.
 */

import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser'
import { getAppConfig } from './app-config'

let _msalInstance: PublicClientApplication | null = null

/**
 * Initialize MSAL with runtime configuration.
 * Must be called after `loadAppConfig()` has resolved.
 */
export function initializeMsal(): PublicClientApplication {
    const config = getAppConfig()

    const msalConfig: Configuration = {
        auth: {
            clientId: config.azureAdClientId || '',
            authority: `https://login.microsoftonline.com/${config.azureAdTenantId || 'common'}`,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin,
        },
        cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false,
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message) => {
                    if (level === LogLevel.Error) {
                        console.error('[MSAL]', message)
                    }
                },
                logLevel: LogLevel.Error,
            },
        },
    }

    _msalInstance = new PublicClientApplication(msalConfig)
    return _msalInstance
}

/**
 * Get the singleton MSAL instance.
 * Throws if called before `initializeMsal()`.
 */
export function getMsalInstance(): PublicClientApplication {
    if (!_msalInstance) {
        throw new Error('MSAL not initialized — call initializeMsal() first')
    }
    return _msalInstance
}

/** Scopes requested when the user logs in. */
export const loginScopes = {
    scopes: ['User.Read'],
}
